import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Skeleton,
  Grow,
  Card,
  Box
} from "@mui/material";
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";

import availabilityLottie from "../assets/animations/availability.json";
import { SCHEDULE_STATE } from "../types";
import { CancelInterviewDialog } from "../components/Dialogs/CancelInterviewDialog";
import { useSchedulerStore } from "../state/scheduler.store";
import { SubmittedAvailabilities } from "../components/Meeting/SubmittedAvailabilities";
import moment from "moment-timezone";
import { formatDurationForMeetingDescription } from "../utils/duration";
import { filterAndSortFutureAvailabilities } from "../utils/availability";
import { PoweredByText } from "../components/Branding/PoweredBy";
import { TbTimezone } from "react-icons/tb";
import { TbCalendarMonth } from "react-icons/tb";
import { useI18nLanguage } from "../hooks/i18nLanguage";
import { AltChatUI } from "../components/Oliver/AltChatUI";
import { useScreenSize } from "../hooks/useScreenSize";
import { getAllDatesFromRanges } from "../utils/slots";

interface IAvailabilityReviewProps {
  status: SCHEDULE_STATE | null;
  handleCancel: (id: string) => Promise<void>; // declare promise
  handleReschedule: () => void;
}

export const AvailabilityReview: React.FC<IAvailabilityReviewProps> = ({
  status,
  handleCancel
}) => {
  const { selectedLanguageForMoment } = useI18nLanguage();
  const { t } = useTranslation();
  const [isCancelling, setIsCancelling] = useState(false);
  const { ui, link, meetingLink, actions, sessionData } = useSchedulerStore();
  const { isMediumScreen, isSmallScreen } = useScreenSize();
  const [showCancelDialogState, setShowCancelDialogState] = useState(false);

  const handleCancelDialog = async () => {
    if (link?.scheduleId) {
      setIsCancelling(true);
      setShowCancelDialogState(false);
      await handleCancel(link?.scheduleId);
      setIsCancelling(false);
    }
  };

  const handleEditTimes = () => {
    actions.setSessionData({
      ...(sessionData ?? {}),
      isRescheduleFlow: true,
      requestSessionStartedAt: Date.now()
    });
    actions.setUiState({
      currentStep: "initial",
      currentView: "availability-slot",
      workDayView: true
    });
  };

  const submittedAvailabilities = useMemo(() => {
    return meetingLink?.scheduledData?.availabilityDocData?.availability ?? [];
  }, [meetingLink?.scheduledData?.availabilityDocData]);

  const availabilityTimezone = useMemo(() => {
    return (
      meetingLink?.scheduledData?.availabilityDocData?.clientTimeZone ??
      moment.tz.guess()
    );
  }, [meetingLink?.scheduledData?.availabilityDocData]);

  const futureSubmittedAvailabilities = useMemo(() => {
    const futureEvents = filterAndSortFutureAvailabilities(
      submittedAvailabilities,
      availabilityTimezone
    );

    // Filter to only include availabilities within valid date ranges
    const validDates =
      meetingLink?.scheduledData?.docDataRooster?.selectionMode ===
      "individual_dates"
        ? meetingLink?.availability?.range?.flatMap((i) => [
            moment(i.rangeStart).tz(availabilityTimezone).format("YYYY-MM-DD"),
            moment(i.rangeEnd)
              .tz(availabilityTimezone)
              .subtract(1, "millisecond")
              .format("YYYY-MM-DD")
          ])
        : getAllDatesFromRanges(
            meetingLink?.availability?.range ?? [],
            availabilityTimezone
          );

    // Filter out weekends and past dates
    const filteredValidDates = validDates?.filter((date) => {
      const day = moment(date).day();
      return (
        day !== 0 && day !== 6 && moment(date).endOf("day").isAfter(moment())
      );
    });

    // Only return availabilities that fall within valid dates
    return futureEvents.filter((availability) => {
      const availabilityDate = moment(availability.start)
        .tz(availabilityTimezone)
        .format("YYYY-MM-DD");
      return filteredValidDates?.includes(availabilityDate) ?? false;
    });
  }, [
    submittedAvailabilities,
    availabilityTimezone,
    meetingLink?.availability?.range,
    meetingLink?.scheduledData?.docDataRooster?.selectionMode
  ]);

  // If URL doesn't have 'review' parameter, set UI state
  useEffect(() => {
    if (meetingLink?.status !== "availability_received") {
      actions.setUiState({
        currentStep: "initial",
        currentView: "availability-slot",
        workDayView: true
      });
    }
  }, [meetingLink?.status]);

  useEffect(() => {
    if (!futureSubmittedAvailabilities.length) {
      actions.setUiState({
        currentStep: "initial",
        currentView: "availability-slot",
        workDayView: true
      });
    }
  }, [futureSubmittedAvailabilities]);

  return (
    <Grow in={true} style={{ transformOrigin: "1 2 2" }} timeout={150}>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100dvh" }}
        flex={1}
      >
        <Stack direction="column">
          <Card
            variant="outlined"
            raised={false}
            sx={{ borderWidth: { xs: "0px", md: "1px" } }}
          >
            <Stack
              direction="column"
              flex={1}
              alignItems="center"
              sx={{ px: 3, width: { sm: "100vw", md: "535px" } }}
            >
              <CancelInterviewDialog
                show={showCancelDialogState}
                onCancel={handleCancelDialog}
                onClose={() => setShowCancelDialogState(false)}
              />

              {/* Animation */}
              <Stack sx={{ pt: 1 }}>
                {ui?.currentStep === "scheduling" ? (
                  <Skeleton
                    variant="circular"
                    height="150px"
                    width="150px"
                    sx={{ mt: 3, mb: 3 }}
                  />
                ) : (
                  <Lottie
                    animationData={availabilityLottie}
                    style={{
                      height: "180px"
                    }}
                    loop={false}
                  />
                )}
              </Stack>

              <Stack
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ paddingBottom: "30px", minWidth: "100%" }}
              >
                {/* Title */}
                <Typography
                  component="h1"
                  fontSize={"23px"}
                  fontWeight={500}
                  textAlign="center"
                  width="100%"
                >
                  {t("ROOSTER.PUBLIC-APP.AVAILABILITY_TIME_SELECTION", {
                    defaultValue: "Availability Time Selection"
                  })}
                </Typography>

                <Stack alignItems="center" justifyContent="center">
                  <Typography
                    fontSize="15px"
                    color="textSecondary"
                    textAlign="center"
                    sx={{
                      px: "12px"
                    }}
                  >
                    {t("ROOSTER.PUBLIC-APP.AVAILABLE_INTERVIEW_TIMES", {
                      defaultValue:
                        "You have the following times showing as available for your interview lasting at least {{duration}}",
                      duration: formatDurationForMeetingDescription(
                        meetingLink?.availability?.duration ?? 0,
                        t,
                        selectedLanguageForMoment
                      )
                    })}
                  </Typography>
                </Stack>

                {/* Selected Times List */}
                {meetingLink?.mode === "availability" &&
                  status !== SCHEDULE_STATE.MEETING_CANCELLED &&
                  ui?.currentStep !== "loading" && (
                    <>
                      <Divider orientation="horizontal" flexItem />
                      <Stack
                        direction="column"
                        sx={{
                          minWidth: "100%",
                          px: { xs: 1, md: 5 },
                          pt: 2,
                          pb: 1
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={4}
                          alignContent="flex-start"
                        >
                          <Stack>
                            <Typography
                              fontSize="15px"
                              fontWeight={500}
                              textTransform="uppercase"
                              color="textSecondary"
                              display="flex"
                              alignItems="center"
                            >
                              <TbCalendarMonth size="23px" /> &nbsp;&nbsp;
                              {t("ROOSTER.PUBLIC-APP.WHEN", {
                                defaultValue: "When"
                              })}
                            </Typography>
                          </Stack>
                          <Stack>
                            {meetingLink?.scheduledData?.availabilityDocData
                              ?.clientTimeZone && (
                              <SubmittedAvailabilities
                                submittedAvailabilities={
                                  futureSubmittedAvailabilities
                                }
                                availabilityTimezone={availabilityTimezone}
                              />
                            )}
                          </Stack>
                        </Stack>

                        <Stack>
                          <Typography
                            fontSize="16px"
                            fontWeight={500}
                            color="textSecondary"
                            display="flex"
                            alignItems="flex-start"
                            width="100%"
                          >
                            <TbTimezone size="22px" /> &nbsp;&nbsp;
                            {t("ROOSTER.PUBLIC-APP.TIMEZONE_LABEL", {
                              defaultValue: "Timezone : {{timezone}}",
                              timezone:
                                meetingLink?.scheduledData?.availabilityDocData
                                  ?.clientTimeZone
                            })}
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
              </Stack>

              {/* Footer */}
              {status === SCHEDULE_STATE.AVAILABILITY &&
                ui?.currentStep !== "loading" && (
                  <>
                    <Divider orientation="horizontal" flexItem />{" "}
                    <Stack
                      direction="row"
                      flex={1}
                      spacing={3}
                      alignItems="center"
                      sx={{ padding: "10px" }}
                    >
                      <Typography
                        fontSize="15px"
                        color="text.secondary"
                        data-testid="need-change-text"
                      >
                        {t("ROOSTER.PUBLIC-APP.NEED_CHANGE", {
                          defaultValue: "Need to make a change ?"
                        })}
                      </Typography>
                      <span>
                        {!isCancelling && (
                          <Button
                            size="small"
                            onClick={handleEditTimes}
                            disabled={!meetingLink?.canReschedule}
                          >
                            {t("ROOSTER.PUBLIC-APP.EDIT_TIMES", {
                              defaultValue: "Edit Times"
                            })}
                          </Button>
                        )}
                        &nbsp; &nbsp; &nbsp;
                        <Button
                          size="small"
                          color="error"
                          sx={{
                            ":disabled": { opacity: 0.8 }
                          }}
                          disabled={
                            meetingLink?.canCancel === false || isCancelling
                          }
                          onClick={() => setShowCancelDialogState(true)}
                        >
                          {isCancelling && (
                            <CircularProgress
                              size={15}
                              variant="indeterminate"
                              thickness={6}
                              sx={{ color: "error.main", mr: "10px" }}
                              aria-label={t("ROOSTER.PUBLIC-APP.CANCELLING", {
                                defaultValue: "Cancelling"
                              })}
                            />
                          )}
                          {isCancelling
                            ? t("ROOSTER.PUBLIC-APP.CANCELLING", {
                                defaultValue: "Cancelling"
                              })
                            : t("ROOSTER.PUBLIC-APP.CANCEL_INTERVIEW", {
                                defaultValue: "Cancel Interview"
                              })}
                        </Button>
                      </span>
                    </Stack>
                  </>
                )}
            </Stack>
          </Card>
          <PoweredByText show />
        </Stack>
        <AltChatUI
          view="availability-review"
          showChatIcon={isMediumScreen || isSmallScreen}
        />
      </Stack>
    </Grow>
  );
};
